// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the board model
//  ---------------------------------------------------------------------------
//  What a board IS, as data: a named row in a list, a scene of elements, and
//  the images those elements point at. No React, no canvas, no Excalidraw —
//  md-board.jsx draws one and md-app.jsx decides which is open. Both ask this.
//
//  WHY IT IS ITS OWN MODULE. A board is the first thing this app stores that
//  has no natural size. A day's records are a handful of short strings written
//  by a person typing; a board is however many strokes somebody felt like
//  making, plus whatever they pasted into it. Everything below follows from
//  that one difference.
//
//  WHERE ONE LIVES. Scenes and images are in IndexedDB (app/md-boarddb.js),
//  reached asynchronously; the list of boards — a handful of small rows — is
//  still a localStorage document, which is why the index functions here are
//  synchronous and the drawing functions are not. The move happened on
//  2026-08-14, the day a real board met the ~5 MB localStorage wall and lost;
//  md-boarddb.js's header keeps the whole story. Drawings stored before the
//  move are migrated at boot and healed one at a time on read, and a legacy
//  copy is never deleted before its replacement is confirmed stored.
//
//  THE DOCUMENT CAP, AND WHY IT STOPPED BEING A WALL. server/sync.js caps a
//  document at MAX_VALUE_BYTES, and a board used to be the only thing here that
//  could approach it. The answer was to STRAND the drawing — saved here in
//  full, left out of the push, and named on a held list so its absence was not
//  read as a deletion. That worked, and it was a rail around a hole rather than
//  a floor over one: the user was told their desk would not reach their iPad
//  and given nothing to do about it except draw less.
//
//  Nothing a desk holds approaches the cap now, because the two things that
//  could have both been given NAMES instead of payloads:
//
//    • a DRAWING past the cap spills to its own bytes. `board:<id>` becomes
//      { spill: { hash, size, blob: 1 }, elements, at } — a few dozen
//      characters — and mdBoardLoad resolves it back. See mdBoardSave.
//    • an IMAGE is a name, a mime type and a size. Never a base64 payload.
//      See mdBoardFilePut.
//
//  Both go through the one door, md-blobs.ts, filed under the desk that owns
//  them. mdBoardHeldKeys is therefore empty, and stays — see its own comment
//  for why a retired rail keeps its shape rather than disappearing.
//
//  A DOCUMENT DECLARES ITS BYTES. `blob: 1` beside a hash is what md-barrier.ts
//  reads, and the barrier is what stops a name travelling before the object it
//  names. A document that forgot the marker syncs early and lands on the other
//  device as a broken picture with a perfectly valid reference underneath it,
//  and nothing on that device can tell whether the bytes are coming or gone.
//
//  AND A NAME IS NOT A DRAWING. A device can hold a desk's document without
//  holding its bytes — that is every second device, between the pull and the
//  fetch. mdBoardLoad answers such a desk with an empty scene, because the
//  canvas has to mount somehow, and mdBoardSave then REFUSES to write over it:
//  the first stroke on an empty canvas must never become the whole desk on
//  every device. It is the 2026-08-18 loss one layer down, and the correction
//  is the same sentence — absence is not emptiness, and a device may only speak
//  for what it holds.
//
//  IMAGES WRITTEN BEFORE THIS STAY READABLE FOR EVER. They are on devices and
//  inside backup files. A data-URL document is migrated in place on the way
//  past — idempotently, because the hash IS the name — and a migration that
//  cannot happen leaves the only copy exactly where it is.
//
//  IMAGES LIVE BESIDE THE BOARD, NOT INSIDE IT. Excalidraw keeps its files in
//  a map hanging off the scene, which would put a 200 KB screenshot inside the
//  same document as the drawing and blow the cap on the first paste. Each file
//  is its own document instead — storage unit = sync unit, the same rule the
//  rest of the app follows — keyed by a hash of its own bytes, so dropping the
//  same picture in twice stores it once.
//
//  WHAT IS NOT SYNCED: which board you are looking at. Two devices should be
//  able to sit on different boards; that is a property of a screen, not of an
//  account.
//
//  A KNOWN EDGE, stated rather than hidden: deleting a board removes its row
//  and its scene here, and the row's disappearance travels because the list is
//  one last-write-wins document. The deleted board's own scene document is left
//  on the server, unreferenced, until the data layer grows real tombstones for
//  keys outside a day. It costs storage nobody sees; it cannot resurrect a
//  board, because nothing points at it.
// ════════════════════════════════════════════════════════════════════════════

/** A row in the board switcher. The drawing itself lives under its own key.

    `kind` is one field of foresight, and it is here rather than later on purpose.
    A board is on its way to being a SPACE rather than a drawing — the same
    rectangle arranged as a dashboard, a table, a kanban — and the index is one
    last-write-wins document, so adding a discriminator to it after two devices
    are already syncing means a migration on both. Absent means `'draw'`; nothing
    writes `'draw'`, for the same reason a record's type is never written. */
interface MdBoardMeta {
  id: string;
  name: string;
  at: number;
  /* When this ROW last changed — a rename, a re-scope — as distinct from `at`,
     which is when the desk was made and which the Librarian reads as the desk's
     time. They were one field until the list started merging, and conflating
     them means either a rename that cannot win a merge or a rename that moves
     the desk to the top of every catalogue sorted by when things happened.
     Absent on every row written before the list merged: read as zero, which
     makes an untouched row lose to a touched one and two untouched rows resolve
     the way they always did — the arriving copy wins. */
  edited?: number;
  kind?: string;
  scopeRef?: string;
  pid?: string;
}

/** uid → when it was deleted. The shape a day's tombstones have had since the
    beginning, and deliberately the same one: it is already proven, and
    md-merge.ts's merger already knows it. */
type MdDeskTombstones = Record<string, number>;

/** A drawing, as it is stored: Excalidraw's elements plus the settings that
    belong to the board rather than to the window looking at it. */
interface MdBoardScene {
  elements: unknown[];
  appState: Record<string, unknown>;
}

/** A picture as it was stored until story 1.7, and as it is still handed to
    Excalidraw: the whole thing, base64, inside a JSON document. Documents in
    this shape are on devices and inside backup files, and they are read for
    ever — see healFile, which turns one into the shape below on the way past. */
interface MdBoardFile {
  id: string;
  mimeType: string;
  dataURL?: string;
  created: number;
  blobHash?: string;
  byteLength?: number;
}

/** …and a picture as it is STORED and SYNCED now: the name of its own bytes.

    `blob: 1` is the marker md-barrier.ts reads, and it is not decoration — an
    undeclared hash is a document that travels before the object it points at.
    `mimeType` rather than `mime` because that is what the shape above calls it
    and what Excalidraw asks for: one name for one thing, across both shapes
    and across the backup format.

    `id` is still the ENGINE's — the file id written onto the image element —
    and `hash` is the bytes' own name. They answer different questions, and two
    desks can point at one object under two ids. */
interface MdBoardFileRef {
  id: string;
  mimeType: string;
  hash: string;
  size: number;
  blob: 1;
  created: number;
}

type MdBoardFileDoc = MdBoardFile | MdBoardFileRef;

/** A drawing too big for one document, stored as the name of its own bytes.

    `elements` is a COUNT where a scene has an array, which is what makes the
    two tellable apart without a version field — and it is the one thing worth
    knowing about a desk this device cannot currently read. */
interface MdBoardSpill {
  spill: { hash: string; size: number; blob: 1 };
  elements: number;
  at: number;
}

(function (g: any) {
  'use strict';

  const K_INDEX = 'md:boards';           // the list — synced, one document; stays in localStorage (a handful of small rows)
  const K_TOMBS = 'md:deskTombstones';   // which desks are gone, and when — synced as `desk-tombstones`
  const TOMBS_DOC = 'desk-tombstones';   // …under this key on the wire
  const K_BOARD = 'md:board:';           // + id — LEGACY localStorage key per drawing; scenes live in IndexedDB now
  const K_FILE = 'md:boardfile:';        // + hash — LEGACY localStorage key per image; files live in IndexedDB now
  const K_OPEN = 'md:boardOpen';         // which one this screen is on — LOCAL

  /* The keys scenes and files are stored under in IndexedDB — and they ARE the
     sync keys, no translation: what md-sync pushes is byte-for-byte what is
     stored, and md-backup folds these documents back under their legacy 'md:'
     names, so a backup file cannot tell the move happened. See md-boarddb.js
     for why the move happened at all (the ~5 MB wall) — the short version is
     that localStorage is a fixed, tiny, per-origin bucket and a board is the
     first thing this app stores that has no natural size. */
  const DB_BOARD = 'board:';
  const DB_FILE = 'boardfile:';

  /* Mirrors MAX_VALUE_BYTES in server/sync.js. Duplicated rather than shared
     because the client and the Worker have no module in common — and a silent
     drift here is the exact failure this guards against, so the number is
     checked by a test that reads the server's source. */
  const MAX_DOC_BYTES = 1024 * 1024;
  /* An image is NOT held to that, and this number answers a different question:
     what will THIS DEVICE keep? The server's cap is what the server will accept.
     Two questions, two constants, and conflating them is what broke pasting.

     It used to say 192 KB, justified like this: "It is stored as base64 in
     localStorage, where the origin gets about 5 MB for everything the app has
     ever saved." That stopped being true on 2026-08-14, when files moved to
     IndexedDB (md-boarddb.js). The number outlived its reason by five days and
     then spent months refusing ordinary screenshots — about 144 KB of actual
     picture, once base64's third is taken off. md-file.ts:106 keeps 40 MB in the
     same kind of store without trouble.

     8 MB of base64 is roughly a 6 MB photograph, which covers a phone
     screenshot and a camera roll picture with room to spare. Past that the
     honest answer is that the thing being pasted is not a board image.

     Still refused BEFORE it is written, because a refused paste is a message the
     user can act on and a full quota later is not.

     A PICTURE BETWEEN THE TWO CAPS USED TO BE KEPT AND HELD BACK — raising
     this without that rail would have offered a 4 MB value to a 4 MB push and
     had the whole batch refused on every sync, for ever, arriving days later
     as "my phone stopped updating". There is no longer anything between the
     two caps: a picture's DOCUMENT is a name and a size, whatever the picture
     weighs, so the only question this number still answers is the one it was
     written for. See mdBoardFilePut. */
  const MAX_FILE_BYTES = 8 * 1024 * 1024;

  const UNTITLED = 'Untitled desk';
  const DESK_DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const DESK_MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

  /** The calm, useful name of a Desk created without one. Written by hand
      rather than through Intl so commas, locale punctuation, and casing cannot
      make the same Desk convention look different on two devices. */
  function mdBoardDateName(at: unknown = Date.now()): string {
    const date = new Date(typeof at === 'number' && Number.isFinite(at) ? at : Date.now());
    return `${DESK_DAYS[date.getDay()]} ${date.getDate()} ${DESK_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
  }

  /** The date name is the right name for a desk exactly once a day. A second
      one made before midnight would wear the first one's label in the
      catalogue, in Recents and in search, with nothing on any of those screens
      to tell them apart — so the second one says which it is.

      A GAP IS FILLED rather than stepped over: delete "Desk (2)" and the next
      desk takes that name back, because a shelf somebody just cleared should
      not keep counting. Case and padding are not a difference anyone can see
      on a card, so they are not a difference here. */
  function mdBoardFreeName(base: unknown, taken: unknown): string {
    const want = typeof base === 'string' ? base.trim() : '';
    if (!want) return want;
    const held = new Set((Array.isArray(taken) ? taken : [])
      .map((name) => (typeof name === 'string' ? name.trim().toLocaleLowerCase() : ''))
      .filter(Boolean));
    if (!held.has(want.toLocaleLowerCase())) return want;
    /* Bounded by the shelf: with n names held, one of the first n + 1 candidates
       must be free, so this cannot run away on a corrupt list. */
    for (let n = 2; n <= held.size + 2; n += 1) {
      const candidate = `${want} (${n})`;
      if (!held.has(candidate.toLocaleLowerCase())) return candidate;
    }
    return want;
  }

  /** Where ONE desk's row stands, given where the whole index stands and when
      the index was last agreed with the server (mdSyncSeenAt).

      A DESK'S ROW TRAVELS INSIDE ONE DOCUMENT, so the document's own state is
      the only thing a card could ask about until the list started merging —
      and asking it made renaming one desk light the mark on every card on the
      shelf. True about the document; false about three of the four desks.

      Now each row carries `edited`, so the rows can be told apart: a row that
      has not moved since the last agreement is exactly what the server holds,
      whatever else in the list has changed.

      IT OVER-REPORTS RATHER THAN UNDER-REPORTS, deliberately and in one place:
      a row pulled from a device whose clock runs ahead of ours carries a stamp
      later than our agreement and reads as changed until the next sync. A mark
      that says there is work when there is none costs a glance; the other
      direction is a desk that looks safe and is not.

      Pure over plain data — no store, no clock — so the rule is provable. */
  function mdBoardRowState(row: any, indexState: unknown, seenAt: unknown): unknown {
    /* A DOCUMENT THAT AGREES WITH THE SERVER SPEAKS FOR EVERY ROW IN IT, and
       so does one this device has never sent: nothing in it has travelled. */
    if (indexState !== 'changed') return indexState;
    const agreed = Number.isFinite(seenAt as number) ? (seenAt as number) : 0;
    if (agreed <= 0) return indexState;
    /* `edited` is when the ROW last moved and is the answer whenever it is
       there — including when it is EARLIER than `at`, which two writes inside
       one millisecond can produce, since a new row's stamp is nudged forward to
       keep the shelf's order. `at` is the fallback for every row written before
       the list merged: an old desk made long before the last sync really is
       settled, which is how a shelf full of them stops lying the day this ships
       rather than after each one has been renamed once. A row with neither has
       nothing to stand on and keeps the document's answer. */
    const moved = Number.isFinite(row && row.edited) ? row.edited
      : (Number.isFinite(row && row.at) ? row.at : 0);
    return moved > 0 && moved <= agreed ? 'synced' : indexState;
  }

  /* See MdBoardMeta.kind. Never written — absent is this. */
  const MD_BOARD_DEFAULT_KIND = 'draw';

  /* Where a pinned record's link points. A FIXED origin, not this device's:
     the link is stored inside the board document and syncs, so an origin read
     off `location` would put `http://localhost:53437` on somebody's phone.

     Nothing has to be clicked — the card renders in place and IS the thing you
     interact with — but the address is real rather than a made-up scheme, and
     that matters for the one affordance we do not control: Excalidraw draws its
     own link indicator on any element carrying a link, and there is no hook to
     take it off just ours. Following it opens ManDana, because wrangler.jsonc
     sets `not_found_handling: single-page-application` and an unknown path
     serves the app shell. So the worst case is landing in the app rather than on
     an error page — and when the client learns to read `/r/<ref>` on boot, every
     board already written points at the right record with no migration. */
  const MD_RECORD_LINK = 'https://mandana.app/r/';
  const MD_NAVIGATION_LINK = 'https://mandana.app/n/';

  /* What survives a save. Everything Excalidraw keeps in appState that is about
     THIS window — where it is scrolled, what is selected, who is collaborating,
     which menu is open — is dropped: syncing it would drag the other device's
     viewport around while somebody is drawing on it. What is left is the
     board's own settings and the pen you last drew with.

     `viewBackgroundColor` is deliberately NOT here. The paper is white in light
     and black in dark, decided by Settings and by nothing else, so a colour
     stored per board could only ever disagree with the app around it. */
  const APP_STATE_KEEP = new Set([
    'gridSize', 'gridModeEnabled', 'objectsSnapModeEnabled',
    'currentItemStrokeColor', 'currentItemBackgroundColor', 'currentItemFillStyle',
    'currentItemStrokeWidth', 'currentItemStrokeStyle', 'currentItemRoughness',
    'currentItemOpacity', 'currentItemFontFamily', 'currentItemFontSize',
    'currentItemTextAlign', 'currentItemStartArrowhead', 'currentItemEndArrowhead',
    'currentItemRoundness', 'currentItemArrowType',
  ]);

  /* ── ids ─────────────────────────────────────────────────────────────────
     Lowercase alphanumeric and nothing else. This is not cosmetic: the id ends
     up inside a sync key and server/sync.js matches keys against a pattern, so
     an id with a colon or a slash in it is a board that silently never syncs. */
  function newId(): string {
    let s = '';
    while (s.length < 10) s += Math.random().toString(36).slice(2);
    return s.slice(0, 10);
  }

  /* A content hash, so the same image dropped in twice is stored once. Two
     different 32-bit hashes plus the length, because one 32-bit hash over
     image data collides more often than anybody would like and a collision
     here means one picture silently becoming another. */
  function hashOf(s: string): string {
    let a = 2166136261, b = 5381;
    for (let i = 0; i < s.length; i++) {
      const c = s.charCodeAt(i);
      a = Math.imul(a ^ c, 16777619);
      b = ((b << 5) + b + c) | 0;
    }
    const u = (n: number) => (n >>> 0).toString(36);
    return `${u(a)}${u(b)}${s.length.toString(36)}`;
  }

  function bytesOf(value: unknown): number {
    try { return JSON.stringify(value).length; } catch (e) { return Infinity; }
  }

  /* ── the one door, and what a build without it does ────────────────────────
     md-blobs.ts is listed above this module in index.html, so in the app it is
     always here. It is asked for anyway, exactly the way idbUp() asks about
     IndexedDB below and for the same reason: a build that has not got it is on
     the floor it has always had — a picture is a data URL, a drawing past the
     cap is stranded and named as held — rather than being broken. Degraded,
     not dead. Every rule this file retires is retired only where the door is,
     and every one of them still stands underneath. */
  function blobsUp(): boolean {
    return typeof g.mdBlobPut === 'function' && typeof g.mdBlobGet === 'function';
  }

  /* Lowercase hex, the spelling md-blobs.ts, md-barrier.ts and server/blobs.js
     all use. One spelling, or the same bytes have two names. */
  const HASH = /^[0-9a-f]{64}$/;
  const isHash = (value: unknown): value is string =>
    typeof value === 'string' && HASH.test(value);

  /* Base64 in chunks, as md-backup.js does it and for the same reason:
     String.fromCharCode takes an argument list rather than a river, and a
     four-megabyte picture handed over whole overflows the call stack. */
  const CHUNK = 0x8000;

  function toBase64(bytes: Uint8Array): string {
    let s = '';
    for (let i = 0; i < bytes.length; i += CHUNK) {
      s += (String.fromCharCode as any).apply(null, bytes.subarray(i, i + CHUNK));
    }
    return g.btoa(s);
  }

  function fromBase64(text: string): Uint8Array {
    const s: string = g.atob(text);
    const out = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i += 1) out[i] = s.charCodeAt(i);
    return out;
  }

  /** The mime type and the base64 body of a data URL, or null.

      ONLY `;base64,`. A percent-encoded data URL is a string with a charset
      question attached, and answering that wrong turns a picture into
      different bytes filed under a name that says they are the same — the one
      mistake content addressing cannot recover from, because every device that
      later fetches them fails the check and nothing can say why. Anything this
      cannot read keeps its old shape, which is always readable. */
  function partsOf(dataURL: string): { mime: string; body: string } | null {
    if (dataURL.indexOf('data:') !== 0) return null;
    const at = dataURL.indexOf(';base64,');
    if (at < 0) return null;
    const head = dataURL.slice(5, at);
    const semi = head.indexOf(';');
    return { mime: semi < 0 ? head : head.slice(0, semi), body: dataURL.slice(at + 8) };
  }

  /** Bytes this device can produce for a hash, or null.

      Goes to the store when the cache misses, which is how a desk drawn on the
      laptop opens on the phone. Null is a real answer rather than an error:
      the bytes are somewhere this device cannot reach right now. */
  async function bytesFor(hash: unknown): Promise<Uint8Array | null> {
    if (!blobsUp() || !isHash(hash)) return null;
    try {
      const bytes = await g.mdBlobGet(hash);
      return bytes && typeof bytes.length === 'number' ? bytes as Uint8Array : null;
    } catch (e) { return null; }
  }

  /** Put bytes through the door and answer the name they were given.

      A REFUSAL IS STILL A NAME. mdBlobPut caches the bytes and lists the hash
      as owed before it ever asks the store, so a device drawing on a train has
      the picture and can name it; md-barrier.ts is what keeps the document
      naming it from travelling first. Null means the bytes could not be named
      AT ALL — no door, or no hash from it — and a caller holding one must
      leave whatever it was about to replace exactly where it is. */
  async function named(bytes: Uint8Array, type: string, ref: string): Promise<{ hash: string; size: number } | null> {
    if (!blobsUp()) return null;
    let put: any = null;
    try { put = await g.mdBlobPut(bytes, { type, ...(ref ? { ref } : {}) }); }
    catch (e) { put = null; }
    return put && isHash(put.hash) ? { hash: put.hash, size: bytes.length } : null;
  }

  /** `board:<id>` for the desk that owns some bytes.

      A hold belongs to a RECORD and not to a screen: 0.7's per-record
      references are what story 2.2 reclaims storage with, and bytes filed
      under the wrong desk are bytes released when the wrong desk goes. The
      caller names the desk wherever it knows it. Where it does not, the desk
      this screen is on is the honest answer — READ rather than minted, because
      mdBoardOpenId() creates a desk when there is none, and pasting a picture
      is not a reason to invent one. */
  function deskRef(id: unknown): string {
    if (typeof id === 'string' && id) return `board:${id}`;
    const open = g.mdRead(K_OPEN, null);
    return typeof open === 'string' && open ? `board:${open}` : '';
  }

  /* ── the list ────────────────────────────────────────────────────────────── */

  function readIndex(): MdBoardMeta[] {
    const raw = g.mdRead(K_INDEX, []);
    return Array.isArray(raw) ? raw.filter(isMeta) : [];
  }

  function isMeta(row: any): row is MdBoardMeta {
    return !!row && typeof row === 'object' && typeof row.id === 'string' && !!row.id;
  }

  /* ── when a row last changed, and when a desk stopped existing ────────────
     Two numbers, and the whole of the list's merge is decided by comparing
     them. `stampOf` is deliberately the LATER of the two fields: a row from
     before this build has no `edited`, and reading that as zero would make it
     lose to a tombstone raised before the desk was even made. */
  function stampOf(row: any): number {
    const edited = row && Number.isFinite(row.edited) ? row.edited : 0;
    const at = row && Number.isFinite(row.at) ? row.at : 0;
    return Math.max(edited, at);
  }

  function readTombs(): MdDeskTombstones {
    const raw = g.mdRead(K_TOMBS, {});
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const out: MdDeskTombstones = {};
    for (const [id, at] of Object.entries(raw)) {
      if (id && Number.isFinite(at as number)) out[id] = at as number;
    }
    return out;
  }

  /** Fold deletions in, never out. mdMergeTombstones is the same function the
      day store uses, and it is used here rather than copied for the reason
      md-merge.ts states about itself: a tombstone that can be lost is a
      deletion that comes back, on every device at once. */
  function writeTombs(tombs: MdDeskTombstones): MdDeskTombstones {
    const merged = g.mdMergeTombstones
      ? g.mdMergeTombstones(readTombs(), tombs)
      : Object.assign({}, readTombs(), tombs);
    if (g.mdWrite(K_TOMBS, merged) && typeof g.mdSyncTouch === 'function') g.mdSyncTouch(TOMBS_DOC);
    return merged;
  }

  /** The list, with anything the account has deleted taken out of it.
      A tombstone only wins against a row OLDER than the deletion — the rule
      md-merge.ts has enforced for records since the beginning, and the reason a
      deletion can be permanent without being a ban on the id. */
  function withoutDeleted(rows: MdBoardMeta[], tombs: MdDeskTombstones): MdBoardMeta[] {
    return rows.filter((row) => {
      const gone = tombs[row.id];
      return !(typeof gone === 'number' && Number.isFinite(gone) && stampOf(row) <= gone);
    });
  }

  /** Two devices' lists, unioned by id.
      ---------------------------------------------------------------------------
      A ROW IS THE UNIT, not the document. Both halves of this matter and they
      fail in opposite directions: taking the incoming list whole loses every
      desk the other device has not heard of yet, and keeping the local list
      whole means a rename never arrives. So each id is resolved on its own, by
      the stamp the row carries, and an id only one side has is simply kept.

      An id both sides hold with the SAME stamp resolves to the incoming copy —
      which is exactly what the whole-list rule did, so two devices that have
      never touched a row still agree the way they always did. */
  function mergeIndex(local: MdBoardMeta[], incoming: MdBoardMeta[]): MdBoardMeta[] {
    const out: MdBoardMeta[] = [];
    const seen = new Map<string, number>();
    for (const row of local) { seen.set(row.id, out.length); out.push(row); }
    for (const row of incoming) {
      const at = seen.get(row.id);
      if (at === undefined) { seen.set(row.id, out.length); out.push(row); continue; }
      if (stampOf(row) >= stampOf(out[at])) out[at] = row;
    }
    return out;
  }

  /** A row has just been edited here — a rename, a re-scope. Stamped so the
      change can win a merge without pretending the desk was made now. */
  function touch(row: MdBoardMeta): MdBoardMeta {
    row.edited = Date.now();
    return row;
  }

  /** The one order this list is ever stored in.

      A MERGED LIST HAS TO BE THE SAME LIST ON BOTH DEVICES, byte for byte, and
      until 5.x it was not. mergeIndex keeps the local rows where they are and
      appends the ones it has not seen, so a laptop and a phone that each made
      a desk ended with the same two desks in opposite orders. mdHash reads an
      array in order — correctly, because reordering a list is a real edit
      everywhere else in this app — so each device read the other's copy as
      work it had not got, merged it, produced its own order again, and pushed.
      One in, one out, every sync, for ever, on the most ordinary action there
      is. Both shelves showed both desks the whole time.

      OLDEST FIRST IS NOT AN ARBITRARY TIE-BREAK. mdBoardCreate appends, so
      this is the order every shelf is already in; what changes is that it is
      now the order on BOTH of them. The same account used to show its desks
      differently depending on which screen you were looking at, and nothing
      about that was ever decided — it was whichever device happened to have
      pulled which list.

      The id breaks a tie, because two desks made in the same millisecond on
      two devices must still land the same way round on both. */
  function canonical(rows: MdBoardMeta[]): MdBoardMeta[] {
    return [...rows].sort((a, b) => {
      const at = (Number.isFinite(a.at) ? a.at : 0) - (Number.isFinite(b.at) ? b.at : 0);
      return at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
    });
  }

  function writeIndex(rows: MdBoardMeta[]): boolean {
    const ok = g.mdWrite(K_INDEX, canonical(rows));
    if (ok && typeof g.mdSyncTouch === 'function') g.mdSyncTouch('boards');
    /* THE SHELF HAS TO HEAR ABOUT THIS, and until now it only heard when it
       was the one who wrote. A pulled index changed nothing on screen: a desk
       made on the laptop was simply not on the iPad until a reload, and "sync
       is broken" is the only reading available to the person holding it.

       Announced from the WRITE rather than from the pull, because there is one
       write and several writers — a create, a rename, a delete, and an applied
       document — and a signal wired to any one of them is a signal the others
       are missing. See useBoards in md-board.jsx for what must NOT happen when
       this is heard. */
    if (ok && typeof g.dispatchEvent === 'function' && typeof g.CustomEvent === 'function') {
      try { g.dispatchEvent(new g.CustomEvent('mdboardschanged')); }
      catch (e) { /* a listener's fault is not the write's */ }
    }
    return ok;
  }

  function mdBoardIndex(): MdBoardMeta[] {
    return readIndex();
  }

  /** What kind of space a board is. Absent means the drawing it has always been. */
  function mdBoardKind(row: unknown): string {
    const k = row && typeof row === 'object' ? (row as { kind?: unknown }).kind : null;
    return typeof k === 'string' && /^[a-z][a-z0-9]{0,15}$/.test(k) ? k : MD_BOARD_DEFAULT_KIND;
  }

  /* ── a record pinned to a board ──────────────────────────────────────────
     A PIN IS AN ELEMENT, NOT A SECOND STORE. The obvious design is a `refs`
     array beside the scene; this is better on every axis and the difference is
     not stylistic:

       • it saves and syncs through the path that already exists — no new key,
         no KEY_PATTERN change, no server deploy, and the document cap already
         covers it;
       • an embeddable is BINDABLE, so an arrow drawn to a pinned record FOLLOWS
         it, which is the whole of "bring in records to connect them" and is not
         reachable from a parallel array at all;
       • it moves, resizes, groups, z-orders and undoes with everything else on
         the board, because it IS everything else on the board.

     The ref lives in the element's `link`. Excalidraw renders an embeddable by
     asking `renderEmbeddable` first and only falling back to an iframe when that
     returns null — so md-board.jsx draws a real ManDana card there and no iframe
     is ever created. See its header, which called this shot before projects
     existed. */

  /** The link that carries a ref onto a board. Empty for a ref that is not one. */
  function mdBoardRecordLink(ref: unknown): string {
    const key = g.mdRecordRefKey ? g.mdRecordRefKey(ref) : '';
    return key ? MD_RECORD_LINK + key : '';
  }

  /** …and the ref back out of a link. Null for a link that is not one of ours. */
  function mdBoardLinkRef(link: unknown): any {
    if (typeof link !== 'string' || link.indexOf(MD_RECORD_LINK) !== 0) return null;
    return g.mdRecordRefParse ? g.mdRecordRefParse(link.slice(MD_RECORD_LINK.length)) : null;
  }

  /** A destination already owned by this account, carried without cloning it
      into a record. The canonical navigation target is the identity, while the
      fixed HTTPS wrapper keeps Excalidraw's embeddable contract and survives
      sync to devices with different origins. */
  function mdBoardNavigationLink(target: unknown): string {
    if (typeof target !== 'string' || typeof g.mdNavigationPlace !== 'function'
      || typeof g.mdNavigationTarget !== 'function') return '';
    const place = g.mdNavigationPlace(target);
    const canonical = place ? g.mdNavigationTarget(place) : null;
    return canonical ? MD_NAVIGATION_LINK + encodeURIComponent(canonical) : '';
  }

  function mdBoardLinkNavigation(link: unknown): string | null {
    if (typeof link !== 'string' || link.indexOf(MD_NAVIGATION_LINK) !== 0) return null;
    try {
      const raw = decodeURIComponent(link.slice(MD_NAVIGATION_LINK.length));
      const place = typeof g.mdNavigationPlace === 'function' ? g.mdNavigationPlace(raw) : null;
      return place && typeof g.mdNavigationTarget === 'function' ? g.mdNavigationTarget(place) : null;
    } catch (_) { return null; }
  }

  /* What a pinned record occupies. Wide enough for a title and a line of meta at
     the card's real type sizes, and the same 20px corner every card in the app
     has — the element carries the geometry, md-board.jsx draws inside it. */
  const PIN_W = 260;
  const PIN_H = 92;
  const NAVIGATION_W = 420;
  const NAVIGATION_H = 260;

  /* What a record carrying media opens at. A title card is a label and a line of
     meta; a video is a picture, and 260×92 would letterbox it into a slot. The
     ratio is md-media.ts's MD_MEDIA_ASPECT, not a number chosen here, and it is
     spelled out rather than computed because md-board.ts loads before md-media
     does and a model should not depend on load order to know its own geometry. */
  const MEDIA_W = 480;
  const MEDIA_H = 270;                                  // 480 / 270 === 16 / 9

  /* A picture is not 16:9 and pretending it is puts every portrait photo in a
     letterbox. Its true shape is not knowable when the element is built — that
     needs the bytes — so this is a neutral rectangle to start from and the
     handles do the rest; the image is drawn `contain`, so whatever lands here is
     the right shape inside the wrong box rather than a stretched one. */
  const IMAGE_W = 400;
  const IMAGE_H = 300;

  /* A document is read, not watched, so it opens PORTRAIT — roughly the
     proportions of the page inside it. A4 is 1:1.414; this is 1:1.29, a little
     squarer, because the browser's own viewer puts a toolbar across the top and
     matching the paper exactly would leave the page itself cropped. */
  const FILE_W = 420;
  const FILE_H = 540;

  /* A manifestation is an APPLICATION, not a picture or a page. It opens wider
     than a video because a dashboard has columns and a table has rows, and it is
     landscape rather than portrait because it is read the way a board is read
     rather than the way a document is. Deliberately not the largest thing that
     would fit: a portal is something you arrange next to other thinking, and
     anything wanting the whole surface has full screen for that. */
  const MANIFEST_W = 520;
  const MANIFEST_H = 380;

  /** A complete Excalidraw element for one pinned record, or null.

      Built here rather than in the view because it is DATA that gets written to
      a synced document, and data that gets written is data worth asserting.
      Every field the engine expects is stated: a partial element is accepted by
      updateScene and then fails later, somewhere else, on a device that is not
      this one. */
  /* A size is taken WHOLE or not at all. A zero or negative dimension is an
     element with no area — it saves, it syncs, and it is invisible and
     unselectable on every device — and honouring one good side while replacing
     the other produces a rectangle nobody asked for, which is harder to explain
     than simply getting the card. Either both sides are real, or this is a card. */
  function sized(size: unknown): { width: number; height: number } {
    const s = size as { width?: unknown; height?: unknown } | null;
    const ok = (v: unknown) => typeof v === 'number' && Number.isFinite(v) && v > 0;
    if (!s || typeof s !== 'object' || !ok(s.width) || !ok(s.height)) {
      return { width: PIN_W, height: PIN_H };
    }
    return { width: s.width as number, height: s.height as number };
  }

  function embeddable(link: string, kind: string, x: unknown, y: unknown, size?: unknown): any {
    if (!link) return null;
    const now = Date.now();
    const box = sized(size);
    return {
      type: 'embeddable',
      id: newId() + newId(),
      x: typeof x === 'number' && Number.isFinite(x) ? x : 0,
      y: typeof y === 'number' && Number.isFinite(y) ? y : 0,
      width: box.width,
      height: box.height,
      angle: 0,
      strokeColor: 'transparent',
      backgroundColor: 'transparent',
      fillStyle: 'solid',
      strokeWidth: 1,
      strokeStyle: 'solid',
      roughness: 0,
      opacity: 100,
      groupIds: [],
      frameId: null,
      index: null,
      roundness: null,
      seed: Math.floor(Math.random() * 2 ** 31),
      version: 1,
      versionNonce: Math.floor(Math.random() * 2 ** 31),
      isDeleted: false,
      boundElements: null,
      updated: now,
      link,
      locked: false,
      customData: { md: kind },
    };
  }

  function mdBoardPinElement(ref: unknown, x: unknown, y: unknown, size?: unknown): any {
    return embeddable(mdBoardRecordLink(ref), 'record', x, y, size);
  }

  function mdBoardNavigationElement(target: unknown, x: unknown, y: unknown, size?: unknown): any {
    const asked = size || { width: NAVIGATION_W, height: NAVIGATION_H };
    return embeddable(mdBoardNavigationLink(target), 'navigation', x, y, asked);
  }

  function mdBoardCreate(name?: string, scopeRef?: string): MdBoardMeta {
    const clean = typeof name === 'string' ? name.trim() : '';
    const scope = typeof scopeRef === 'string' && g.mdRecordRefParse && g.mdRecordRefParse(scopeRef)
      ? scopeRef : null;
    const parsed = scope && g.mdRecordRefParse(scope);
    const rows = readIndex();
    /* ONE MILLISECOND APART, AT LEAST. The shelf is ordered by `at` and ties
       are broken by id, which is random — so two desks made inside the same
       millisecond would land in an order nobody chose. A person cannot do
       that; a template, an import, or a test makes three in a row without
       trying. Nudging the stamp forward keeps the order the one they were
       made in, and costs a millisecond of accuracy in the only case where the
       millisecond was never the point. */
    const last = rows.length ? rows[rows.length - 1] : null;
    const since = last && Number.isFinite(last.at) ? last.at : 0;
    const now = Math.max(Date.now(), since + 1);
    /* A NAME A PERSON TYPED IS THEIRS, collision or not — two desks called
       "Ideas" is a statement about how they work. Only the name we chose for
       them has to be told apart from the one we chose this morning. */
    const named = clean || mdBoardFreeName(mdBoardDateName(now), rows.map((b) => b.name));
    const board: MdBoardMeta = { id: newId(), name: named, at: now,
      ...(scope ? { scopeRef: scope } : {}),
      ...(parsed && parsed.type === 'project' ? { pid: parsed.id } : {}) };
    writeIndex([...rows, board]);
    return board;
  }

  function mdBoardRename(id: string, name: string): boolean {
    const rows = readIndex();
    const row = rows.find((b) => b.id === id);
    if (!row) return false;
    const clean = typeof name === 'string' ? name.trim() : '';
    row.name = clean || UNTITLED;
    touch(row);
    return writeIndex(rows);
  }

  function mdBoardScope(id: unknown): string | null {
    const row = typeof id === 'string' ? readIndex().find((board) => board.id === id) : null;
    return row && typeof row.scopeRef === 'string' ? row.scopeRef : null;
  }

  /* WHAT MAY HOLD A DESK IS NOW THE CONTAINMENT RULE, not a list of three
     kinds. It used to be `['domain','pursuit','project']`, which made a desk
     inside a desk and a desk inside a record structurally impossible — and
     those are exactly the shapes md-contain.ts's `<=` exists to keep open. The
     rank check still refuses everything the old list did: nothing above a
     project can be contained BY a desk, because a desk is a leaf.

     `pid` is DERIVED here rather than written, which is the correction. It used
     to be set only when the scope happened to be a project, so a desk scoped to
     a pursuit pointed up with `scopeRef` and had no `pid` at all — the answer to
     "what is above this?" depended on which kind the answer was. It now names
     the nearest project at or above the scope, which is what every caller
     reading it already meant. md-parent.ts is the door that knows the whole
     tree; this stays callable on its own for the sync applier, which is
     restoring a decision rather than taking one. */
  function mdBoardSetScope(id: unknown, scopeRef: unknown): boolean {
    const rows = readIndex();
    const row = rows.find((board) => board.id === id);
    if (!row) return false;
    if (scopeRef == null || scopeRef === '') {
      delete row.scopeRef; delete row.pid; touch(row); return writeIndex(rows);
    }
    const parsed = typeof scopeRef === 'string' && g.mdRecordRefParse ? g.mdRecordRefParse(scopeRef) : null;
    if (!parsed) return false;
    if (g.mdContainAllows && !g.mdContainAllows(parsed.type, 'board')) return false;
    row.scopeRef = g.mdRecordRefKey(parsed);
    const pid = parsed.type === 'project' ? parsed.id
      : g.mdParentProjectAbove ? g.mdParentProjectAbove(row.scopeRef) : '';
    if (pid) row.pid = pid; else delete row.pid;
    touch(row);
    return writeIndex(rows);
  }

  async function mdBoardDelete(id: string): Promise<boolean> {
    const rows = readIndex();
    if (!rows.some((b) => b.id === id)) return false;
    /* THE DELETION IS RECORDED, not merely performed. A row that is simply
       absent from this device's list says nothing the other device can act on
       — and since the list unions rather than replaces, an absence now says
       nothing AT ALL. Any device still holding the row would hand it back on
       its next push and the desk would return, named and empty, exactly as it
       did when the list was last-write-wins.

       Written BEFORE the row goes, so a failure between the two leaves the
       account with a deletion it will apply rather than a desk it has quietly
       forgotten to delete. */
    writeTombs({ [id]: Date.now() });
    writeIndex(rows.filter((b) => b.id !== id));
    /* Both stores: the scene lives in IndexedDB now, but a legacy copy may
       still be sitting in localStorage if the migration never reached it —
       and a delete that misses one copy is a drawing that resurrects. */
    if (idbUp()) await g.mdBoardDbDelete(DB_BOARD + id);
    g.mdRemove(K_BOARD + id);
    unreadable.delete(id);
    held.delete(id);
    return true;
  }

  /* ── which board this screen is on ───────────────────────────────────────── */

  function mdBoardOpenId(): string {
    const rows = readIndex();
    const open = g.mdRead(K_OPEN, null);
    if (typeof open === 'string' && rows.some((b) => b.id === open)) return open;
    /* Either nothing has been opened yet or the board that was open is gone.
       Fall back to the first real board, and only invent one when there are
       none — a canvas with no board on it has nothing to show. */
    const first = rows[0] ?? mdBoardCreate();
    g.mdWrite(K_OPEN, first.id);
    return first.id;
  }

  function mdBoardSetOpen(id: string): boolean {
    return g.mdWrite(K_OPEN, id);
  }

  /* ── the drawing ─────────────────────────────────────────────────────────── */

  /* ── the precision a stroke is stored at ───────────────────────────────────
     Nearly all of a drawing's weight is noise nobody can see. The engine records
     a pointer sample exactly as the browser reports it, so one point arrives as
     [418.20000000000005, 233.60000000000002] — thirty-six characters to say
     "418.2, 233.6". An Apple Pencil samples at about 120 Hz, and because a
     stylus sets `simulatePressure` to false every stroke also carries a parallel
     `pressures` array at the same precision. One two-second stroke is twelve
     kilobytes, so a 256 KB cap held about twenty-one of them: a few sentences
     of handwriting, and then "this board is too big to sync".

     Two decimal places is a hundredth of a scene pixel, on coordinates
     Excalidraw smooths before it draws them. It is invisible, and it takes the
     stored scene to under a third of its size — locally as well as on the wire,
     which matters because localStorage is the tighter of the two ceilings.

     Only `points` and `pressures` are touched. Rounding every number on an
     element would catch `angle`, which is in RADIANS: 0.01 there is half a
     degree of rotation, and half a degree is visible on anything with a straight
     edge. */
  const POINT_DP = 2;

  function round(n: unknown): unknown {
    return typeof n === 'number' && Number.isFinite(n) ? Number(n.toFixed(POINT_DP)) : n;
  }

  /* Anything that is not a number, or not the [x, y] tuple the engine documents
     as LocalPoint, is handed back untouched. A future point shape would lose the
     saving rather than corrupt the drawing. */
  function thinElement(el: any): any {
    if (!el || typeof el !== 'object') return el;
    const points = Array.isArray(el.points) ? el.points : null;
    const pressures = Array.isArray(el.pressures) ? el.pressures : null;
    if (!points && !pressures) return el;                 // most elements: no copy at all
    const out = { ...el };
    if (points) out.points = points.map((p: any) => (Array.isArray(p) ? p.map(round) : p));
    if (pressures) out.pressures = pressures.map(round);
    return out;
  }

  /* Applied on the way IN as well as on the way out — this is the one function
     every save, every load and every pulled document passes through. A device on
     an older build still pushes full-precision scenes, and without this they
     would be stored at full precision here for ever after. */
  function sanitiseScene(scene: any): MdBoardScene {
    const raw = Array.isArray(scene && scene.elements) ? scene.elements : [];
    const elements = raw.map(thinElement);
    const src = (scene && typeof scene.appState === 'object' && scene.appState) || {};
    const appState: Record<string, unknown> = {};
    for (const key of Object.keys(src)) if (APP_STATE_KEEP.has(key)) appState[key] = src[key];
    return { elements, appState };
  }

  /* ── is there anything to save ─────────────────────────────────────────────
     A one-line summary of everything a save WOULD write, so "has this scene
     changed" can be answered without writing it.

     THE DEFECT THIS EXISTS FOR. Excalidraw's onChange is a render notification,
     not a change notification: the engine calls it whenever it re-renders, and
     it re-renders on its own. An open desk with nobody touching it therefore
     saved itself about once a second — hashing an empty scene, writing it to
     IndexedDB, and nudging a sync each time — because "the engine painted a
     frame" and "somebody drew something" arrived through the same callback.

     IT LIVES BESIDE `sanitiseScene` BECAUSE IT MUST AGREE WITH IT, and the
     agreement is the whole safety argument: everything the store keeps has to
     be able to move this string, and everything the store drops must not. Read
     it against the two lines above — the same elements, the same
     APP_STATE_KEEP. A key added there and forgotten here is a setting that
     silently stops being saved when it is the only thing you changed.

     An element is summarised by its version, which is what the engine bumps on
     every mutation and what the collaboration boundary already trusts (see
     elementStamp in md-collab.ts). An element with NO version is summarised by
     its bytes instead — a merged, pulled or older-build element is not the
     engine's to promise, and collapsing those to "id, unversioned" would make
     two different drawings look identical and drop a real save.

     Files are named, not read. The picture's bytes are stored beside the scene
     as their own documents; what the scene needs to notice is one arriving or
     leaving. */
  function mdBoardSceneStamp(scene: any, files?: any): string {
    const elements = Array.isArray(scene && scene.elements) ? scene.elements : [];
    let out = '';
    for (const el of elements) {
      if (!el || typeof el !== 'object') { out += `${JSON.stringify(el)};`; continue; }
      out += Number.isFinite(el.version)
        ? `${el.id}:${el.version}:${Number(el.versionNonce) || 0}:${el.isDeleted ? 1 : 0};`
        : `${JSON.stringify(el)};`;
    }
    const src = (scene && typeof scene.appState === 'object' && scene.appState) || {};
    out += '|';
    for (const key of APP_STATE_KEEP) {
      if (Object.prototype.hasOwnProperty.call(src, key)) out += `${key}=${JSON.stringify(src[key])};`;
    }
    const ids = files && typeof files === 'object' ? Object.keys(files) : [];
    return `${out}|${ids.sort().join(',')}`;
  }

  /* ── where a drawing lives ──────────────────────────────────────────────
     IndexedDB, with the legacy localStorage key read through and healed. The
     rules, in order of how much they matter:

       1. A legacy copy is deleted ONLY after its replacement is confirmed
          stored. A migration that erases the original before the copy exists
          is not a migration, it is a coin flip.
       2. A browser with no IndexedDB keeps the old floor entirely — degraded
          to the ~5 MB bucket it always had, not broken.
       3. A FAILED IndexedDB write does not fall back to localStorage: the
          small bucket is where this bug came from, and splitting one drawing
          across two stores is how a device ends up holding half a scene. */

  function idbUp(): boolean {
    return !!(g.mdBoardDbAvailable && g.mdBoardDbAvailable());
  }

  /** The read could not be answered — which is a different fact from "there is
      nothing here", and the difference is what this whole file turns on.

      A symbol rather than a flag on the value, because every caller of readDoc
      already branches on `== null` and a symbol is the one thing that cannot
      be mistaken for a document: it fails isSpill, it fails `== null`, and a
      caller that forgets it gets a visible wrong answer rather than a quiet
      one. Private to this module; the outside world asks mdBoardUnreadable. */
  const FAULT = Symbol('board read fault');

  /* Read a document, looking in the new store first and the legacy key second.
     A legacy hit is healed on the spot — written through, and the old copy
     removed only when the write confirms — so a board that was never touched
     by the boot migration still arrives, one open at a time.

     A FAILED read falls through to the legacy key like an empty one — a copy
     still sitting in localStorage is a real answer and this device holds it —
     and only then answers FAULT, because there was genuinely nowhere left to
     look. */
  async function readDoc(dbKey: string, legacyKey: string): Promise<any> {
    let faulted = false;
    if (idbUp()) {
      const res = await g.mdBoardDbRead(dbKey);
      if (res && res.ok === false) faulted = true;
      else if (res && res.value != null) return res.value;
    }
    const raw = g.mdReadRaw(legacyKey);
    if (raw == null) return faulted ? FAULT : null;
    let value: any;
    try { value = JSON.parse(raw); } catch (e) { return null; }   // corrupt: keep the key, take nothing
    if (idbUp()) {
      const put = await g.mdBoardDbPut(dbKey, value);
      if (put.ok) g.mdRemove(legacyKey);
    }
    return value;
  }

  /* Write a document. Returns the truth, and names WHY on failure — quota
     versus blocked is the difference between "delete something" and "leave
     private browsing", and the banner can only say it if it is known here. */
  /* THE ONE DOOR EVERY DESK DOCUMENT IS WRITTEN THROUGH, which is what lets the
     shelf's mark say anything at all about a drawing. A scene lives in
     IndexedDB, so no render can read it and mdSyncStates cannot see it; naming
     the document here — a string, in localStorage — is how the card gets an
     answer with no I/O in it. See mdSyncTouch. Only an accepted write counts:
     a document that failed to store is not one this device is holding back. */
  async function writeDoc(dbKey: string, legacyKey: string, value: any): Promise<{ ok: boolean; quota: boolean }> {
    const noted = (ok: boolean) => {
      if (ok && typeof g.mdSyncTouch === 'function') g.mdSyncTouch(dbKey);
      /* AND THE ROOM, WHICH CANNOT SEE THIS WRITE. Every other tier 1 family
         announces itself by being written at the localStorage chokepoint,
         which md-live.ts watches. Desks stopped being written there in 1.7 and
         1.8 — so from that day a stroke reached the wire on no device, and the
         drawing arrived on the other one at the speed of the next ordinary
         sync. Nothing failed, because the other four legs of the family were
         all in place; it simply went quiet. See mdLiveOffer, which holds every
         guard and the coalescing window, so this stays one line and cannot
         drift from the policy.

         BOTH PATHS, and the double announcement on the legacy one is harmless:
         the outbox is keyed by document, so a write that is both watched and
         announced is one frame. */
      if (ok && typeof g.mdLiveOffer === 'function') g.mdLiveOffer(dbKey, value);
      return ok;
    };
    if (idbUp()) {
      const res = await g.mdBoardDbPut(dbKey, value);
      return { ok: noted(!!res.ok), quota: !!res.quota };
    }
    const ok = g.mdWrite(legacyKey, value);
    const last = !ok && g.mdStorageHealth ? g.mdStorageHealth().lastFault : null;
    return { ok: noted(ok), quota: !!(last && last.quota) };
  }

  /* ── a drawing too big for a document ─────────────────────────────────────
     Past the cap the document stops being the drawing and becomes its name.
     The bytes are the scene's own JSON, so nothing about the scene changes —
     what changes is where it rests and how it travels. */

  function isSpill(doc: any): doc is MdBoardSpill {
    return !!doc && typeof doc === 'object' && !!doc.spill && typeof doc.spill === 'object'
      && !!doc.spill.blob && isHash(doc.spill.hash);
  }

  /* Desks whose drawing this device cannot get at, and WHY — because the two
     reasons need different words on screen and different second chances:

       'spill' — the document is a name whose bytes have not arrived from the
                 other device yet. Nothing is wrong here; waiting is the cure.
       'fault' — the store could not answer at all. Something IS wrong here,
                 it is this device's own, and it may already be over.

     Remembered rather than asked, because mdBoardSave must refuse to write
     over one and a save fires every 600 ms while somebody is drawing — putting
     a second transaction on that path for a case that is rare and does not
     change until the bytes arrive is the wrong trade. mdBoardLoad is what
     fills it in, and md-board.jsx gates the canvas on that read, so the answer
     is always here before the first stroke can be saved. */
  const unreadable = new Map<string, 'spill' | 'fault'>();

  /** Why this desk cannot be drawn, or null when it can.

      The catalogue asks it per card straight after mdBoardLoad — no second
      read, because the load it just did is what wrote the answer down. */
  function mdBoardUnreadable(id: unknown): 'spill' | 'fault' | null {
    return (typeof id === 'string' && unreadable.get(id)) || null;
  }

  /** The scene a stored document holds, or null when it names bytes this
      device has not got.

      NULL IS THE POINT. It is the difference between "this desk is empty" and
      "this desk is elsewhere", and conflating those two is precisely how a
      drawing gets destroyed — see mdBoardSave and mdBoardSyncDocs, which each
      answer it differently and would both be wrong without it. */
  async function sceneOf(stored: any): Promise<MdBoardScene | null> {
    if (stored == null || stored === FAULT) return null;
    if (!isSpill(stored)) return sanitiseScene(stored);
    const bytes = await bytesFor(stored.spill.hash);
    if (!bytes) return null;
    let parsed: any;
    try { parsed = JSON.parse(new TextDecoder().decode(bytes)); }
    catch (e) { return null; }
    return sanitiseScene(parsed);
  }

  /* WHAT THIS DESK'S DRAWING ALREADY IS ON THIS DEVICE, per desk, as the JSON a
     save would produce. Not a cache of the drawing — nothing is ever READ from
     here — only the answer to "would this write change anything", which
     writeDoc has no way to ask: it marks the document dirty and offers it to
     the room on EVERY accepted write.

     That answer is what a glance costs without it. Excalidraw calls back on
     mount, so opening a desk saves the scene it was just handed; the viewport
     is already stripped from what is stored (APP_STATE_KEEP), so those bytes
     are the bytes already on the device — and one tap on an untouched desk
     uploaded the whole drawing and put a frame on the wire. The bigger the
     drawing, the more it cost to look at it.

     THE SCENE RATHER THAN THE DOCUMENT, so a spilled drawing is answered before
     it is serialised and hashed, and its `at` stamp cannot make identical
     content look different. Written only for reads and writes that SUCCEEDED —
     bytes the store refused are not on the device, and a save that repeats them
     must really run. */
  const held = new Map<string, { json: string; oversize: boolean }>();

  function heldJson(clean: unknown): string | null {
    try { return JSON.stringify(clean); } catch (e) { return null; }
  }

  async function putScene(id: string, doc: unknown, oversize: boolean): Promise<{ ok: boolean; quota: boolean; oversize: boolean }> {
    const res = await writeDoc(DB_BOARD + id, K_BOARD + id, doc);
    if (res.ok) unreadable.delete(id);
    return { ok: res.ok, quota: res.quota, oversize };
  }

  /** Write a drawing, spilling it to its own bytes when it will not fit.

      ONE PLACE, because two callers write scenes — a save and a pull — and a
      merge of two large drawings crosses the cap exactly as readily as a save
      does. Splitting the rule in two is how one of the halves would come to
      lack it. */
  async function storeScene(id: string, clean: MdBoardScene, reuse = true): Promise<{ ok: boolean; quota: boolean; oversize: boolean }> {
    const json = heldJson(clean);
    /* ALREADY ON THE DEVICE. A save that stores what is stored is still a
       SUCCESS — the caller is a canvas, and a false here would read as "your
       stroke was not saved" in the one case where everything is fine — it just
       marks nothing dirty and puts nothing on the wire.

       ONLY A SAVE MAY SKIP. `reuse` is false on the pull path, where the store
       has just been read directly and where being wrong is unrecoverable: a
       document removed from IndexedDB by anything other than mdBoardDelete —
       an account switch, a cleared site, a peer's surgery — leaves this memo
       claiming bytes that are not there, and a pull that believed it would
       decline to write the only copy arriving. The 600 ms save loop is what
       this exists for; a pull happens seldom and must simply be true. */
    const was = reuse && json !== null ? held.get(id) : null;
    if (was && was.json === json) return { ok: true, quota: false, oversize: was.oversize };

    const done = (res: { ok: boolean; quota: boolean; oversize: boolean }) => {
      if (res.ok && json !== null) held.set(id, { json, oversize: res.oversize });
      return res;
    };
    /* THE COMMON PATH, and it is deliberately the short one: nothing is
       hashed, nothing is encoded, the door is not opened. Every 600 ms while
       somebody is drawing, this is the whole of what happens. */
    if (bytesOf(clean) <= MAX_DOC_BYTES) return done(await putScene(id, clean, false));

    const bytes = new TextEncoder().encode(json === null ? JSON.stringify(clean) : json);
    const ref = await named(bytes, 'application/json', `board:${id}`);
    /* Nothing could name the bytes. The drawing is written whole, exactly as
       it was before this story, and it says out loud that it will not travel —
       the older floor, reached rather than fallen through. */
    if (!ref) return done(await putScene(id, clean, true));

    return done(await putScene(id, {
      spill: { hash: ref.hash, size: ref.size, blob: 1 },
      elements: clean.elements.length,
      at: Date.now(),
    } as MdBoardSpill, false));
  }

  async function mdBoardLoad(id: string): Promise<MdBoardScene> {
    const stored = await readDoc(DB_BOARD + id, K_BOARD + id);
    /* THE STORE COULD NOT ANSWER, and this is the 2026-08-31 iPad exactly: a
       connection iOS closed while the tab was away, every read failing, every
       desk in the catalogue reading EMPTY under its own correct name. What
       used to happen here is the whole of that bug — the line below CLEARED
       the guard, because it could only imagine one kind of unreadable, so the
       first save wrote the blank canvas down over a drawing that was never
       gone. It is recorded and the canvas mounts blank, as it must; nothing
       else about this desk is believed until a read succeeds. */
    if (stored === FAULT) { unreadable.set(id, 'fault'); held.delete(id); return sanitiseScene(null); }
    const scene = await sceneOf(stored);
    if (scene) {
      unreadable.delete(id);
      /* THE FIRST GLANCE AFTER A LAUNCH is the one this has to survive: the
         canvas mounts with what was just read and saves it straight back, and
         without knowing what is down there that save is a full upload. What
         was read IS what is stored, so the read is the honest place to learn
         it — recorded as a spill if that is how it rests, so the document's own
         `at` stamp is never restamped for content that did not change. */
      const json = heldJson(scene);
      if (json !== null) held.set(id, { json, oversize: !isSpill(stored) && bytesOf(scene) > MAX_DOC_BYTES });
      return scene;
    }
    /* A name whose bytes are not here. The canvas is handed the empty scene it
       would have been handed before, because it has to mount — and the fact is
       written down, because from here on this desk must not be written over.
       See mdBoardSave. */
    if (isSpill(stored)) unreadable.set(id, 'spill'); else unreadable.delete(id);
    /* Nothing readable is down there, so nothing is known about it. An empty
       scene must never be memoised as "already stored": the first real stroke
       would then be a save that changed nothing. */
    held.delete(id);
    return sanitiseScene(null);
  }

  /** Try a desk again, and say where it stands: 'ok', or why not.

      A FAULT is this device's own trouble, so it gets the one cure this device
      has: the cached connection is dropped, so the read below opens a fresh
      one. md-boarddb.js already heals a handle that dies mid-operation; this
      is for the case where that heal was not enough, and it costs one open.

      A SPILL is not this device's trouble — the bytes are elsewhere. The read
      asks the blob door again, which is exactly what it needs; the caller
      pairs it with a pull, because the door can only hand over what has
      arrived. */
  async function mdBoardRetry(id: string): Promise<'ok' | 'spill' | 'fault'> {
    if (unreadable.get(id) === 'fault' && typeof g.mdBoardDbForget === 'function') g.mdBoardDbForget();
    unreadable.delete(id);
    await mdBoardLoad(id);
    return unreadable.get(id) || 'ok';
  }

  /** Whether a drawing for this board has ever been STORED — which is a
      different question from what it contains, and the difference is the whole
      of S-2. mdBoardLoad answers an empty scene for a board nobody has drawn on,
      because that is what the canvas needs to mount; it is indistinguishable
      from a board somebody wiped, and on the push path that ambiguity destroyed
      somebody's drawing. See mdBoardSyncDocs. */
  async function mdBoardHasScene(id: string): Promise<boolean> {
    const stored = await readDoc(DB_BOARD + id, K_BOARD + id);
    /* A read that failed answers TRUE, deliberately. The question is "has one
       ever been stored", and a device that could not look does not get to say
       no — no is the answer that lets something be written over. */
    if (stored === FAULT) return true;
    return stored != null;
  }

  /** Is there anything ON this board? One sentence, in one place, because two
      screens now ask it and they must not be able to disagree: the canvas, to
      decide whether the wipe control has any work to do, and the panel over a
      portrait board, to decide whether to invite you underneath it. A deleted
      element is still IN the array — Excalidraw tombstones rather than removes,
      so undo can bring it back — and it is not on the board. */
  function mdBoardVisibleElements(elements: any): any[] {
    return Array.isArray(elements) ? elements.filter((el: any) => el && !el.isDeleted) : [];
  }

  function mdBoardSceneEmpty(elements: any): boolean {
    return mdBoardVisibleElements(elements).length === 0;
  }

  /* ── zoom, about the middle of the screen ──────────────────────────────────
     The engine draws no zoom buttons in its compact layout — `.zoom-actions`
     lives in a footer that is not rendered below 730px of container, because
     the answer there is meant to be pinch. On the phone desk that leaves no
     readout and no way back to 100%, so the buttons are ours and this is the
     arithmetic behind them.

     It is the engine's arithmetic, read out of `scene/zoom.ts` rather than
     guessed at. Zooming about a point means the scene point under that point
     does not move; the engine's zoom actions pass the centre of the viewport,
     `width/2 + offsetLeft`, and `getStateForZoom` subtracts `offsetLeft` again
     first thing — so the offsets cancel and half the viewport is the whole
     input. A board inset in a page and a board filling the window zoom
     identically, which is the property a test can hold this to.

     Pure, over plain numbers, so "the drawing does not jump" is provable
     without a canvas, a device or a running engine. */
  const ZOOM_STEP = 0.1;
  const ZOOM_MIN = 0.1;
  const ZOOM_MAX = 30;

  /** The scroll and zoom that put `next` on screen without moving the middle. */
  function mdBoardZoomTo(state: any, next: number): { scrollX: number; scrollY: number; zoom: { value: number } } {
    const s = state || {};
    const from = (s.zoom && typeof s.zoom.value === 'number' && s.zoom.value > 0) ? s.zoom.value : 1;
    /* Six places, and the engine's own bounds. 0.7 + 0.1 is 0.7999999999999999,
       and a readout is a number people compare against 100%. */
    const to = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(next * 1e6) / 1e6));
    const hold = (half: number, scroll: number) =>
      scroll + (half - half / from) - (half - half / to);
    return {
      scrollX: hold((s.width || 0) / 2, s.scrollX || 0),
      scrollY: hold((s.height || 0) / 2, s.scrollY || 0),
      zoom: { value: to },
    };
  }

  /** One press of − or +. `dir` is -1 or 1. */
  function mdBoardZoomStep(state: any, dir: number): { scrollX: number; scrollY: number; zoom: { value: number } } {
    const s = state || {};
    const from = (s.zoom && typeof s.zoom.value === 'number' && s.zoom.value > 0) ? s.zoom.value : 1;
    return mdBoardZoomTo(s, from + (dir < 0 ? -ZOOM_STEP : ZOOM_STEP));
  }

  /** Save a drawing.

      `oversize` means it is on this device but will not travel — which now
      needs a build with no blob door to happen at all, because a drawing past
      the cap spills instead.

      `stranded` means it was NOT saved, and that refusal is the whole of trap
      one. This desk's stored document is a NAME whose bytes are not on this
      device: mdBoardLoad handed the canvas an empty scene because it had
      nothing else to hand it, and writing what is on that canvas now would
      assert — with a fresh timestamp and the full authority of a write — that
      a drawing this device has never seen is a blank page. That is the
      2026-08-18 board loss exactly, and it is worth losing the stroke somebody
      just drew to not repeat it.

      `fault` separates the two ways a desk gets there, because the canvas puts
      a sentence on the screen from this answer and the sentences are not
      interchangeable. A spill is another device's business: the bytes exist
      and are on their way. A fault is this device's storage, and telling
      somebody to wait for a laptop that has nothing to send is the same class
      of untruth as calling the desk empty. */
  async function mdBoardSave(id: string, scene: any): Promise<{ ok: boolean; oversize: boolean; quota: boolean; stranded?: boolean; fault?: boolean }> {
    const why = unreadable.get(id);
    if (why) return { ok: false, oversize: false, quota: false, stranded: true, fault: why === 'fault' };
    return await storeScene(id, sanitiseScene(scene));
  }

  /* ── the move ──────────────────────────────────────────────────────────────
     Run once at boot. Every legacy board and board-file key is copied into
     IndexedDB and then, only then, removed from localStorage — the ~5 MB the
     boards were squatting on is handed back to the rest of the app. Reads
     heal one document at a time too (see readDoc), so an interrupted run is
     continued by ordinary use rather than by a retry flag. */
  async function mdBoardMigrate(): Promise<{ moved: number; failed: number }> {
    let moved = 0, failed = 0;
    if (!idbUp() || !(await g.mdBoardDbReady())) return { moved, failed };
    /* Two families, asked for as two families. The union used to be arrived
       at by reading every key on the device and discarding the ones that were
       neither. */
    for (const key of [...g.mdStorageKeys(K_BOARD), ...g.mdStorageKeys(K_FILE)]) {
      const raw = g.mdReadRaw(key);
      if (raw == null) continue;
      let value: any;
      try { value = JSON.parse(raw); } catch (e) { failed += 1; continue; }  // corrupt: leave the only copy where it is
      const put = await g.mdBoardDbPut(key.slice(3), value);                  // 'md:board:x' → 'board:x'
      if (put.ok) { g.mdRemove(key); moved += 1; } else failed += 1;
    }
    return { moved, failed };
  }

  /* ── images ──────────────────────────────────────────────────────────────── */

  function isFileRef(doc: any): doc is MdBoardFileRef {
    return !!doc && typeof doc === 'object' && !!doc.blob && isHash(doc.hash);
  }

  const mimeOf = (doc: any): string =>
    (doc && typeof doc.mimeType === 'string' && doc.mimeType) ? doc.mimeType : 'image/png';

  /** A picture document as the name of its own bytes, or null when it cannot
      become one.

      IDEMPOTENT, and that is what makes migrating safe to do on every read: the
      hash IS the name, so a document that has already been through here comes
      back unchanged, and one that has not produces the same result however many
      times it is asked. `created` is carried across rather than restamped —
      the day a picture was pasted is a fact about the picture, and moving it
      would make two devices disagree about documents that are otherwise
      identical, on a key that is last-write-wins. */
  async function fileRef(id: string, doc: any, boardId?: unknown): Promise<MdBoardFileRef | null> {
    if (isFileRef(doc)) return doc;
    if (!doc || typeof doc.dataURL !== 'string') return null;
    const parts = partsOf(doc.dataURL);
    if (!parts) return null;
    let bytes: Uint8Array;
    try { bytes = fromBase64(parts.body); } catch (e) { return null; }
    const ref = await named(bytes, mimeOf(doc), deskRef(boardId));
    if (!ref) return null;
    return {
      id: typeof doc.id === 'string' && doc.id ? doc.id : id,
      mimeType: mimeOf(doc),
      hash: ref.hash,
      size: ref.size,
      blob: 1,
      created: Number.isFinite(doc.created) ? doc.created : Date.now(),
    };
  }

  /** …and the same document, written back where it was found.

      Healed on the way past, exactly as readDoc heals a legacy localStorage
      key, and for the same reason: a migration only a boot pass performs never
      finishes, because there is always a device that was not booted. The old
      document is replaced only once the new one is confirmed stored, and it is
      not replaced at all when the bytes could not be named — the rule the move
      to IndexedDB was built on, unchanged. */
  async function healFile(id: string, doc: any, boardId?: unknown): Promise<MdBoardFileDoc> {
    if (isFileRef(doc) || !blobsUp()) return doc;
    const ref = await fileRef(id, doc, boardId);
    if (!ref) return doc;
    const res = await writeDoc(DB_FILE + id, K_FILE + id, ref);
    return res.ok ? ref : doc;
  }

  /** Keep a picture. `boardId` is the desk that owns the bytes.

      `oversize` used to answer "will this reach the other devices?" and now
      answers it with `false` on every path that has the door, because the
      document is a name and a name always fits. It stays because the caller
      is deciding what to tell the user about THIS paste, and a build without
      the door still has something to tell them. */
  async function mdBoardFilePut(file: any, boardId?: unknown): Promise<{ ok: true; id: string; oversize: boolean } | { ok: false; error: string }> {
    const dataURL = file && typeof file.dataURL === 'string' ? file.dataURL : '';
    if (!dataURL) return { ok: false, error: 'no_data' };
    /* Refused BEFORE it is written, not after. This is the DEVICE-KEEP cap and
       it is a different question from the server's; past it the honest answer
       is that the thing being pasted is not a desk image, and a refusal is
       something the user can act on where a full quota later is not. */
    if (dataURL.length > MAX_FILE_BYTES) return { ok: false, error: 'too_large' };

    /* The engine names a file by hashing its bytes and writes that id onto the
       image element, so it is kept as given — re-keying it here would leave the
       element pointing at nothing and the picture would return as a grey box.
       Hashing is the fallback for a caller with no id of its own, and for one
       whose id would not survive the sync key pattern. */
    const given = typeof file.id === 'string' && /^[a-z0-9]{6,64}$/.test(file.id) ? file.id : '';
    const id = given || hashOf(dataURL);

    /* Already have this picture. Answered without writing, and healed in
       passing if it is still carrying its payload — a device that pastes the
       same screenshot again is a perfectly good moment to finish the move. */
    const held = await readDoc(DB_FILE + id, K_FILE + id);
    if (held != null) {
      return { ok: true, id, oversize: bytesOf(await healFile(id, held, boardId)) > MAX_DOC_BYTES };
    }

    const record: MdBoardFileDoc = await fileRef(id, {
      id,
      mimeType: typeof file.mimeType === 'string' ? file.mimeType : 'image/png',
      dataURL,
      created: Date.now(),
    }, boardId) ?? {
      id,
      mimeType: typeof file.mimeType === 'string' ? file.mimeType : 'image/png',
      dataURL,
      created: Date.now(),
    };
    /* Measured on the record, not on the dataURL, because the record is what is
       stored and what the server would be asked to accept — the mime type and the
       JSON quoting are part of the document's weight. */
    const oversize = bytesOf(record) > MAX_DOC_BYTES;
    const res = await writeDoc(DB_FILE + id, K_FILE + id, record);
    return res.ok ? { ok: true, id, oversize } : { ok: false, error: res.quota ? 'quota' : 'write_failed' };
  }

  /** A picture, in the shape Excalidraw wants it: a data URL.

      Null when the document names bytes this device has not got. That is a
      grey box on the canvas and nothing worse, which is why a picture is
      allowed to answer null where a drawing is not — an absent image cannot be
      mistaken for an image somebody deleted, because the element pointing at
      it is still on the desk saying otherwise. */
  async function mdBoardFileGet(id: string, boardId?: unknown): Promise<MdBoardFile | null> {
    const stored = await readDoc(DB_FILE + id, K_FILE + id);
    if (!stored || typeof stored !== 'object') return null;
    const doc = await healFile(id, stored, boardId);
    if (!isFileRef(doc)) return typeof doc.dataURL === 'string' ? doc : null;
    const bytes = await bytesFor(doc.hash);
    if (!bytes) return null;
    return {
      id: doc.id || id,
      mimeType: mimeOf(doc),
      dataURL: `data:${mimeOf(doc)};base64,${toBase64(bytes)}`,
      created: Number.isFinite(doc.created) ? doc.created : 0,
    };
  }

  /** Is this picture's DOCUMENT here? Asked by a caller that only wants to
      know whether to store it, and separate from mdBoardFileGet because
      answering it that way now costs a base64 encode of the whole picture —
      the difference between a question and the answer to a different one. */
  async function mdBoardFileHas(id: string): Promise<boolean> {
    return (await readDoc(DB_FILE + id, K_FILE + id)) != null;
  }

  function mdBoardDataBytes(dataURL: unknown): Uint8Array | null {
    if (typeof dataURL !== 'string') return null;
    const comma = dataURL.indexOf(',');
    if (comma < 0 || !/;base64$/i.test(dataURL.slice(0, comma))) return null;
    try {
      const text = g.atob(dataURL.slice(comma + 1));
      const bytes = new Uint8Array(text.length);
      for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i);
      return bytes;
    } catch { return null; }
  }


  /** The files one board's elements point at — what Excalidraw needs handed back. */
  async function mdBoardFilesFor(id: string): Promise<Record<string, MdBoardFile>> {
    const out: Record<string, MdBoardFile> = {};
    for (const el of (await mdBoardLoad(id)).elements) {
      const fileId = (el as any) && (el as any).fileId;
      if (typeof fileId !== 'string' || out[fileId]) continue;
      const file = await mdBoardFileGet(fileId, id);
      if (file) out[fileId] = file;
    }
    return out;
  }

  /** The exact durable documents owned by one visible desk.

      The toolbar asks this instead of looking at the account-wide sync set: a
      changed setting, another desk, or a device row must not light the current
      desk's Sync glyph. An unreferenced picture is deliberately absent too —
      board blobs are retained in this phase, but retention is not unfinished
      work on whichever desk happens to be open. */
  async function mdBoardSyncKeys(id: unknown): Promise<string[]> {
    if (typeof id !== 'string' || !id) return [];
    const stored = await readDoc(DB_BOARD + id, K_BOARD + id);
    /* Absence and a failed read both answer NOTHING, for one reason: this list
       is what a device claims it can speak for, and neither state is a claim. */
    if (stored == null || stored === FAULT) return [];
    /* THE LIST AND THE TOMBSTONES ARE PART OF THIS DESK. "Sync desk" meant the
       drawing and its pictures, so a desk that had just been made, renamed or
       deleted reported itself up to date while the one document that says it
       exists sat unpushed — the desk was on the account and not on the shelf,
       which is the state people described as "it synced but it isn't there".

       They are account-wide documents, so this widens what one desk's sync
       carries. That is the honest shape: you cannot send a desk's existence
       without sending the list it lives in, and the list is one small document
       every device pulls on every sync anyway. */
    const out = [`board:${id}`, 'boards', TOMBS_DOC];
    const seen = new Set<string>();
    for (const el of (await mdBoardLoad(id)).elements) {
      const fileId = (el as any) && (el as any).fileId;
      if (typeof fileId !== 'string' || seen.has(fileId)) continue;
      seen.add(fileId);
      /* A shell can arrive before its bytes. Name the file only when this
         device owns its document; absence is not a deletion and must never
         enable a button that would try to announce one. */
      if (await readDoc(DB_FILE + fileId, K_FILE + fileId) != null) {
        out.push(`boardfile:${fileId}`);
      }
    }
    return out;
  }

  /** The asset announcements one desk owes a joining peer: each image element's
      fileId paired with the hash, size and mime its bytes answer to. Read from
      the stored file documents — where the blob reference actually lives —
      because pairing a hash to a fileId by position would be a guess. */
  async function mdBoardAssetManifests(id: string): Promise<any[]> {
    const out: any[] = [];
    const seen = new Set<string>();
    for (const el of (await mdBoardLoad(id)).elements) {
      const fileId = (el as any) && (el as any).fileId;
      if (typeof fileId !== 'string' || seen.has(fileId)) continue;
      seen.add(fileId);
      const doc = await readDoc(DB_FILE + fileId, K_FILE + fileId);
      if (isFileRef(doc)) {
        out.push({ fileId: doc.id || fileId, hash: doc.hash, size: doc.size, mimeType: mimeOf(doc) });
      }
    }
    return out;
  }

  /** The blob holds this desk owns, whether its scene is small or spilled.

      THIS ANSWER LIVES BESIDE THE DOCUMENT SHAPES. Trash used to inspect only
      `md:board:` and therefore saw every legacy drawing and no current one:
      current scenes and pictures live in IndexedDB, and a spilled scene names
      its pictures only inside the bytes. Repeating that knowledge in the
      deletion door would make the next board format a permanent storage leak.

      A LINKED RECORD IS NOT A FILE ID. Only image elements are followed, so a
      PDF card on this desk remains its record's hold and survives the desk. If
      spilled bytes cannot be reached, the spill itself is still returned and
      the pictures leak rather than being guessed at — the recoverable side of
      a choice between retained storage and somebody else's file. */
  async function mdBoardOwnedBlobs(id: unknown): Promise<string[]> {
    if (typeof id !== 'string' || !id) return [];
    const out = new Set<string>();
    const stored = await readDoc(DB_BOARD + id, K_BOARD + id);
    if (isSpill(stored)) out.add(stored.spill.hash);
    const scene = await sceneOf(stored);
    if (!scene) return [...out];
    for (const el of scene.elements) {
      const fileId = (el as any) && (el as any).fileId;
      if (typeof fileId !== 'string' || !fileId) continue;
      const file = await readDoc(DB_FILE + fileId, K_FILE + fileId);
      if (isFileRef(file)) out.add(file.hash);
    }
    return [...out];
  }

  /** Stop naming a file whose bytes are never coming (1.17/1.18).

      The other half of cancel. mdBlobCancel gives back what the store took and
      answers which reference to drop; this drops it, because the door does not
      know what a desk is and should not learn.

      It matches on the HASH rather than on a file id, because that is what the
      door can say — and because the same picture dropped twice is one object
      with two documents, so forgetting only the first would leave the second
      as a grey box for ever. A document with no hash is a pre-1.7 data URL
      carrying its own bytes: it is not waiting for anything, and matching it
      on a field it does not have would delete somebody's picture to cancel an
      upload that was never theirs.

      `id` is taken and unused: a boardfile document is not scoped to a desk in
      storage, and cancelling an upload means those bytes are not arriving for
      ANY desk that named them. The argument stays because every other verb in
      this file takes it and a caller that had to remember which ones do would
      eventually pass it to the wrong one. */
  async function mdBoardForgetFile(id: string, hash: unknown): Promise<any> {
    if (typeof hash !== 'string' || !/^[0-9a-f]{64}$/.test(hash)) {
      return { ok: false, forgot: [] };
    }
    const forgot: string[] = [];
    for (const key of (g.mdStorageKeys ? g.mdStorageKeys('md:boardfile:') : [])) {
      const doc = g.mdRead(key, null);
      if (!doc || doc.hash !== hash) continue;
      if (g.mdRemove(key)) forgot.push(key.slice('md:boardfile:'.length));
    }
    return { ok: true, forgot };
  }

  /* ── what travels ────────────────────────────────────────────────────────── */

  /** Nothing. And the function stays.

      THE RULE HAS NOT BEEN REPEALED, its condition has stopped occurring. The
      rail is this: md-sync reads "a document that was there at the last sync
      and is not in the local set now" as a DELETION and pushes a null for it,
      so a document that merely fell out of mdBoardSyncDocs() would delete the
      desk on every other device. The drawing would be safe here and destroyed
      there, which is the worst shape a sync bug can take. So a document too
      big to send was NAMED rather than merely absent, and the push loop
      skipped it in both directions.

      Nothing this module writes can be too big any more. A drawing past the cap
      spills to its own bytes and leaves a few dozen characters behind; a
      picture is a name, a mime type and a size. There is nothing left to hold.

      It is retired rather than removed for two reasons. mdHeldKeys in
      md-sync.js calls it and the shape is the contract. And the day something
      here can outgrow a document again, this is the sentence that has to be
      un-retired — deleting it would leave nobody to say so.

      A build with no blob door is on the older floor, and there the rail is
      still the only thing standing between an oversized desk and a deletion on
      the other device. See blobsUp. */
  async function mdBoardHeldKeys(): Promise<string[]> {
    if (blobsUp()) return [];
    const held: string[] = [];
    for (const row of readIndex()) {
      if (bytesOf(await mdBoardLoad(row.id)) > MAX_DOC_BYTES) held.push(`board:${row.id}`);
    }
    /* Pictures too, and this half was missing until 2026-08-19. A drawing over
       the cap was named here; the images beside it were not, because the file cap
       used to sit below the server's and no file could ever exceed it. That made
       192 KB load-bearing for sync safety by accident, and it is exactly the kind
       of invariant that breaks the moment somebody fixes the number it was hiding
       behind. Both halves are asked the same question, in the same place. */
    for (const [key, file] of await mdBoardFileDocs()) {
      if (bytesOf(file) > MAX_DOC_BYTES) held.push(key);
    }
    return held;
  }

  /* Every image document this device holds, keyed by its SYNC key.
     ---------------------------------------------------------------------------
     WHY THIS IS ONE FUNCTION. It answers "where are the files?", and two callers
     need it: the push, which offers the ones that fit, and the held list, which
     names the ones that do not. That enumeration used to live inline in the push
     and nowhere else, which is precisely why the held list forgot about files —
     the knowledge existed in one place and the question was asked in two.

     Files come from wherever they actually are: IndexedDB after the 2026-08-14
     move, plus any legacy localStorage keys an interrupted migration left
     behind. The seen-set keeps one document from being counted twice, and keeps
     a stale localStorage copy from shadowing the current one.

     AND IT IS WHERE THE PAYLOAD DOCUMENTS FINALLY GO. A three-megabyte data
     URL written by an older build is migrated here, on the way into the offer,
     which is what lets mdBoardHeldKeys be empty rather than merely optimistic:
     the push cannot meet a picture it has not already turned into a name. */
  async function mdBoardFileDocs(): Promise<Map<string, MdBoardFileDoc>> {
    const found = new Map<string, MdBoardFileDoc>();
    if (idbUp()) {
      for (const key of await g.mdBoardDbKeys()) {
        if (!key.startsWith(DB_FILE)) continue;
        const file = await g.mdBoardDbGet(key);
        if (file) found.set(key, await healFile(key.slice(DB_FILE.length), file));
      }
    }
    for (const key of g.mdStorageKeys(K_FILE)) {
      const dbKey = key.slice(3);                 // 'md:boardfile:x' -> 'boardfile:x'
      if (found.has(dbKey)) continue;
      /* readDoc rather than a bare read, so a document the boot migration
         never reached goes home to IndexedDB on the way past — the same
         healing every other read here performs, rather than a second path
         that quietly leaves the ~5 MB bucket occupied. */
      const file = await readDoc(dbKey, key);
      if (file) found.set(dbKey, await healFile(dbKey.slice(DB_FILE.length), file));
    }
    return found;
  }

  /** The board documents that can be named without opening IndexedDB — which
      today is the index and nothing else.

      WHY IT IS SPLIT OUT. Every scene lives in IndexedDB, so the full answer
      below can only ever be a promise, and a caller that merely wants to know
      WHICH documents exist would have to become async to ask. The Librarian's
      sync X-ray is that caller: it runs inside a synchronous render, and a
      scene is not a record's home anyway — a board record is kept in this
      index, which is always pushed. So the synchronous half is named here and
      the full walk is built on it, rather than the two being written twice and
      drifting.

      NEVER PUSH FROM THIS. It is deliberately a SUBSET, and md-sync reads a
      document that has gone missing as a deletion; pushing this list would
      delete every board scene on every other device. */
  function mdBoardSyncDocsSync(): Record<string, unknown> {
    return { boards: readIndex(), [TOMBS_DOC]: readTombs() };
  }

  /** What one desk's drawing travels as, or `undefined` when this device may
      not speak for it at all.

      THE RAW DOCUMENT, not mdBoardLoad. A device can hold a board's ROW — the
      index is one small document every device pulls — without holding its
      drawing, and mdBoardLoad answers that state with an empty scene, the same
      value a deliberately wiped board has. Pushing it asserts, with a fresh
      timestamp and the full authority of a write, that a drawing this device
      has never seen is empty.

      That is what destroyed a board on 2026-08-18: an iPad's unpushed drawing,
      overwritten by a laptop that had signed in an hour earlier and had
      nothing but the row. A device may only speak for what it holds. Absence
      is not emptiness, and it is not a deletion either — mdHeldKeys already
      stops a missing document being mourned as one, so skipping here is silent
      in both directions, exactly as an oversized board is. */
  async function sceneDocFor(id: string): Promise<unknown> {
    const stored = await readDoc(DB_BOARD + id, K_BOARD + id);
    /* FAULT joins absence here for the same reason and with the same force: a
       device that could not read the drawing has nothing to say about it, and
       the only scene it could invent is the blank one. */
    if (stored == null || stored === FAULT) return undefined;
    /* A SPILL TRAVELS AS ITSELF, never as the scene it resolves to. It is
       small, so there is nothing to gain by unpacking it — and a device holding
       the name without the bytes would have to INVENT a scene in order to send
       one, which is the paragraph above with an extra step in it. What this
       device holds is a name, so a name is what it says. */
    if (isSpill(stored)) return stored;
    const scene = sanitiseScene(stored);
    /* Unreachable where the blob door is: a drawing that would not fit spilled
       when it was saved, and a merge writes through the same rule. Kept for the
       older floor, where it is still the only thing between an oversized desk
       and a deletion on the other device — skipped rather than pushed and
       rejected, so one desk does not cost the user every other document in the
       batch. mdBoardHeldKeys names it on the way out. */
    if (bytesOf(scene) > MAX_DOC_BYTES) return undefined;
    return scene;
  }

  /** Exactly the named board documents, and nothing else opened to find them.

      THE POINT IS WHAT IT DOES NOT READ. mdBoardSyncDocs below walks every desk
      and every picture on the account out of IndexedDB and hashes them; asking
      it whether ONE desk has something to send read forty desks to answer, and
      md-board.jsx asked it every 120 ms while a sync ran. A question about a
      desk is answered by reading that desk. Same rules, one row at a time —
      they live in sceneDocFor precisely so the two answers cannot disagree. */
  async function mdBoardSyncDocsFor(keys: unknown): Promise<Record<string, unknown>> {
    const out: Record<string, unknown> = {};
    for (const key of Array.isArray(keys) ? keys : []) {
      if (typeof key !== 'string' || !key) continue;
      if (key === 'boards') { out.boards = readIndex(); continue; }
      if (key === TOMBS_DOC) { out[TOMBS_DOC] = readTombs(); continue; }
      if (key.startsWith('board:')) {
        const doc = await sceneDocFor(key.slice('board:'.length));
        if (doc !== undefined) out[key] = doc;
        continue;
      }
      if (!key.startsWith(DB_FILE)) continue;
      const file = await readDoc(key, K_FILE + key.slice(DB_FILE.length));
      if (file == null || file === FAULT) continue;
      const healed = await healFile(key.slice(DB_FILE.length), file);
      if (bytesOf(healed) > MAX_DOC_BYTES) continue;
      out[key] = healed;
    }
    return out;
  }

  /** Every board document this device holds and the server would accept. */
  async function mdBoardSyncDocs(): Promise<Record<string, unknown>> {
    const rows = readIndex();
    const out: Record<string, unknown> = mdBoardSyncDocsSync();
    for (const row of rows) {
      const doc = await sceneDocFor(row.id);
      if (doc !== undefined) out[`board:${row.id}`] = doc;
    }
    /* Every picture is a name by the time it gets here — mdBoardFileDocs
       migrates a payload document on the way past — so this skip cannot fire
       either. It is kept for the same reason and on the same floor. */
    for (const [key, file] of await mdBoardFileDocs()) {
      if (bytesOf(file) > MAX_DOC_BYTES) continue;
      out[key] = file;
    }
    return out;
  }

  /** Whether applying this key MERGES rather than overwrites.

      A scene does, and only when the merger is loaded. THE LIST AND THE
      TOMBSTONES DO TOO, as of 3.1 — and that is the correction, not an
      extension. A desk's drawing merged while the document that decides whether
      the desk EXISTS did not, so the two halves of one desk travelled under
      different rules and the crude one governed the half a person can see. A
      locally-changed list could not be written into at all, which is how a
      stroke arrived for a desk that was not on the shelf.

      The image files stay last-write-wins: a picture is one immutable name, so
      there is nothing in it to merge. */
  function mdBoardMergesDoc(key: unknown): boolean {
    if (key === 'boards' || key === TOMBS_DOC) return true;
    return typeof key === 'string' && key.startsWith('board:') && typeof g.mdMergeScene === 'function';
  }

  /** Write back a board document pulled from another device. */
  async function mdBoardApplyDoc(key: string, value: unknown): Promise<void> {
    /* ARRIVES BEFORE THE LIST OR AFTER IT, and the result is the same either
       way — the same order-independence md-store.js gives a day's tombstones,
       and for the same reason: two documents cross the wire in whatever order
       the server hands them over, and a deletion that only works in one of the
       two orders is a deletion that comes back half the time. */
    if (key === TOMBS_DOC) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return;
      const tombs = writeTombs(value as MdDeskTombstones);
      const rows = readIndex();
      const kept = withoutDeleted(rows, tombs);
      if (kept.length !== rows.length) writeIndex(kept);
      return;
    }
    if (key === 'boards') {
      if (!Array.isArray(value)) return;                 // not a list; not usable
      /* Repair, do not reject — the bias the rest of the sync layer takes. A row
         with no id cannot be repaired into anything, so it goes; a row with no
         name is a board somebody drew on, and it keeps its drawing.

         THE SPREAD IS LOAD-BEARING. This used to rebuild each row from the three
         fields it knew, which quietly DELETED any field a newer build had added
         — and because the index is one last-write-wins document, the older
         device then pushed the flattened row back and the loss travelled to
         every device. `kind` is the first field that would have hit it. Repair
         what is named; carry everything else through untouched. */
      const rows = value.filter(isMeta).map((row: any) => ({
        ...row,
        id: row.id,
        name: typeof row.name === 'string' && row.name.trim() ? row.name : UNTITLED,
        /* NOT Date.now(). A repaired row used to be stamped with the moment
           it was repaired, so applying the same document twice produced
           different bytes: the index never matched the hash this device had
           recorded for it, was offered on every single sync for ever, and —
           because a pull will not overwrite a locally-dirty document — pinned
           the whole account's cursor behind it. Zero is a real answer: this row
           reached us with no time on it, and it has not acquired one. */
        at: Number.isFinite(row.at) ? row.at : 0,
      }));
      /* UNIONED, AND THEN THE DELETIONS ARE APPLIED. Overwriting the local list
         with this one is what lost a desk every time two devices made one
         between syncs: the second push was the whole truth as far as its device
         knew, and the first device's desk left the account with its drawing
         still sitting on the server addressed by nothing.

         The tombstone pass is what keeps this safe. Union alone would mean a
         deleted desk returns the moment any device that still holds the row
         pushes its copy — a deletion nobody can make stick, which is worse than
         the loss it replaces. */
      writeIndex(withoutDeleted(mergeIndex(readIndex(), rows), readTombs()));
      return;
    }
    if (key.startsWith('board:')) {
      const spilled = isSpill(value);
      if (!spilled && (!value || typeof value !== 'object' || !Array.isArray((value as any).elements))) return;
      const id = key.slice('board:'.length);

      const stored = await readDoc(DB_BOARD + id, K_BOARD + id);
      /* THIS DEVICE COULD NOT READ ITS OWN COPY, so it does not know what it
         is about to merge against — and the only thing it could merge against
         is the empty scene a failed read looks like. Merging there quietly
         resolves every local stroke as deleted and then writes the result
         down. Left for the next pull, when the store may answer. */
      if (stored === FAULT) { unreadable.set(id, 'fault'); return; }
      const local = await sceneOf(stored);
      const incoming = await sceneOf(value);

      /* THE OTHER DEVICE'S DRAWING IS A NAME THIS ONE CANNOT READ YET — the
         ordinary state of affairs between pulling a document and fetching the
         object it points at. Taken as-is where there is nothing to lose by
         taking it: this device then HOLDS the name, can hand it on, and
         mdBoardLoad fetches the bytes the moment it is asked to open the desk.
         Where there is something here already, it is left alone, because a
         name is not evidence about a drawing this device can actually read. */
      if (incoming == null) {
        if (stored == null) { await writeDoc(key, K_BOARD + id, value); unreadable.set(id, 'spill'); }
        return;
      }
      /* And the mirror of it: this device's own document is a name it cannot
         read. Nothing about the incoming copy makes the bytes it points at
         wrong, and overwriting the only reference to them would strand a
         drawing nobody can get back. Left for the next pull, by which time the
         object may have arrived. */
      if (local == null && stored != null) return;

      /* MERGED, NOT WRITTEN OVER — the same treatment md-store.js gives a day.
         This used to replace the local scene outright, so of two devices drawn
         on between syncs, one lost its whole session; and a device with nothing
         to say could say it loudly enough to erase a drawing. The engine stamps
         every element it touches, so the two versions can be reconciled stroke
         by stroke instead of document by document. See mdMergeScene.

         An older build with no merger available still writes the incoming copy:
         degraded to what it did before, rather than refusing the pull. */
      const next = local && g.mdMergeScene ? g.mdMergeScene(local, incoming) : incoming;
      /* Through storeScene, so a merge of two large drawings spills exactly as
         a save does. Written any other way, the one document that cannot be
         reached by the rule would be the one the rule most needs. */
      await storeScene(id, sanitiseScene(next), false);
      return;
    }
    if (key.startsWith('boardfile:')) {
      /* BOTH SHAPES. A device on an older build still pushes the whole picture
         as a data URL, and refusing that document would make its pictures
         invisible here — the marker is what this build WRITES, never what it
         demands of everybody else. */
      const ok = isFileRef(value)
        || (!!value && typeof value === 'object' && typeof (value as any).dataURL === 'string');
      if (!ok) return;
      await writeDoc(key, K_FILE + key.slice('boardfile:'.length), value);
    }
  }

  Object.assign(g, {
    mdBoardIndex, mdBoardCreate, mdBoardDateName, mdBoardFreeName, mdBoardRowState, mdBoardRename, mdBoardDelete, mdBoardScope, mdBoardSetScope,
    mdBoardOpenId, mdBoardSetOpen,
    mdBoardLoad, mdBoardHasScene, mdBoardVisibleElements, mdBoardSceneEmpty, mdBoardSceneStamp, mdBoardSave, mdBoardMigrate,
    mdBoardUnreadable, mdBoardRetry,
    mdBoardZoomTo, mdBoardZoomStep,
    MD_BOARD_ZOOM_MIN: ZOOM_MIN, MD_BOARD_ZOOM_MAX: ZOOM_MAX,
    mdBoardFilePut, mdBoardFileGet, mdBoardFileHas, mdBoardFilesFor, mdBoardSyncKeys,
    mdBoardOwnedBlobs, mdBoardForgetFile,
    mdBoardAssetManifests, mdBoardDataBytes, mdBoardToBase64: toBase64,
    mdBoardSyncDocs, mdBoardSyncDocsSync, mdBoardSyncDocsFor, mdBoardApplyDoc, mdBoardMergesDoc, mdBoardHeldKeys,
    mdBoardKind, mdBoardRecordLink, mdBoardLinkRef, mdBoardPinElement,
    mdBoardNavigationLink, mdBoardLinkNavigation, mdBoardNavigationElement,
    MD_BOARD_MAX_DOC_BYTES: MAX_DOC_BYTES,
    MD_BOARD_MAX_FILE_BYTES: MAX_FILE_BYTES,
    MD_BOARD_DEFAULT_KIND, MD_BOARD_PIN_W: PIN_W, MD_BOARD_PIN_H: PIN_H,
    MD_BOARD_NAVIGATION_W: NAVIGATION_W, MD_BOARD_NAVIGATION_H: NAVIGATION_H,
    MD_BOARD_MEDIA_W: MEDIA_W, MD_BOARD_MEDIA_H: MEDIA_H,
    MD_BOARD_IMAGE_W: IMAGE_W, MD_BOARD_IMAGE_H: IMAGE_H,
    MD_BOARD_FILE_W: FILE_W, MD_BOARD_FILE_H: FILE_H,
    MD_BOARD_MANIFEST_W: MANIFEST_W, MD_BOARD_MANIFEST_H: MANIFEST_H,
  });
})(typeof window !== 'undefined' ? window : globalThis);
